<template>
  <TirPmMultiselect
    ref="$multiselect"
    class="task-subtasks-search-dropdown"
    popover-classes="task-subtasks-search-dropdown__popover"
    search-input-label="Поиск по номеру или названию задачи"
    :is-search-local="false"
    popover-width="32.5rem"
    :staticScrollHeight="staticScrollHeight"
    :popover-position="popoverPosition"
    :is-disabled-popover="isDisabled"
    :options="viewOptions"
    :no-teleport="noTeleport"
    :disable-content-blur="disableContentBlur"
    search-input-autofocus
    @on-scroll="onScoll"
    @update-search="updateSearch"
    @enter="onSearch"
    @on-open="onOpen"
    @on-close="onClose"
  >
    <template #target>
      <slot name="target" />
      <TaskDrawer ref="$taskDrawer" @on-create-task="onCreateTask" @on-update-task="onUpdateTask" />
    </template>
    <template #optionList="{ option }">
      <div class="task-subtasks-search-dropdown__option" @click="onSelect(option.Id)">
        <span class="task-subtasks-search-dropdown__option-taskid">{{ option.Id }}</span>
        <span class="task-subtasks-search-dropdown__option-name">{{ option.Name }}</span>
        <div class="task-subtasks-search-dropdown__status">
          <div
            class="task-subtasks-search-dropdown__status-dot"
            :style="{ backgroundColor: option.StatusColor }"
          />
          <span
            class="task-subtasks-search-dropdown__status-name"
            :style="{ color: option.StatusColor }"
            >{{ option.StatusName }}</span
          >
          <div
            class="task-subtasks-search-dropdown__status-bg"
            :style="{ backgroundColor: option.StatusColor }"
          />
        </div>
      </div>
    </template>
    <template #optionListBottom>
      <TirPmRouterLink :to="parentTaskDetailPath" :isDisabled="!inDrawer" target="_blank">
        <TirPmButton
          v-if="taskId && allowCreateSubtask"
          :size="TirPmButtonSizeEnum.Regular"
          :variant="TirPmButtonVariantEnum.LinkState"
          style="width: 100%"
          @click="openCreateSubtask"
        >
          Создать новую
          <template #leftSlot>
            <PlusIcon />
          </template>
        </TirPmButton>
      </TirPmRouterLink>
    </template>
  </TirPmMultiselect>
</template>

<script lang="ts" setup>
import { computed, PropType, ref } from 'vue';
import {
  PopoverPositionType,
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
  TirPmMultiselect,
  TirPmRouterLink,
} from 'tir-components';
import { SubtasksSearchFunctionType } from './types';
import { TaskOptionModel, TasksRequestModel } from './models';
import { debounce } from 'tir-pm-debounce';
import { TaskDrawer, TaskDrawerSettingsModel } from '../task-drawer';
import { PlusIcon } from 'tir-style-system/icons/outline';
import { TaskModel } from '../task-drawer/models';
import { useRouter } from 'vue-router';

/**Роутер */
const router = useRouter();

/**Параметры */
const props = defineProps({
  /**Редактируемая задача */
  editTaskInfo: { type: Object as PropType<TaskModel> },
  /**Внутри шторки */
  inDrawer: { type: Boolean, default: false },
  /**ID очереди */
  queueId: { type: String },
  /**ID задачи */
  taskId: { type: Number },
  /**Функция поиска */
  searchFunction: {
    type: Function as PropType<SubtasksSearchFunctionType>,
    required: true,
  },
  /**Шаг поиска */
  step: {
    type: Number,
    default: 10,
  },
  /**Отключить поповер */
  isDisabled: {
    type: Boolean,
    default: false,
  },
  /**Позиционирование по правому краю */
  isRight: {
    type: Boolean,
    default: false,
  },
  /**Игнорирование задач */
  ignoreTasks: {
    type: Array as PropType<Array<number>>,
    default: [],
  },
  /**Кастомная позиция поповера */
  customPopoverPosition: {
    type: Object as PropType<PopoverPositionType>,
  },
  /**Не телепортировать */
  noTeleport: {
    type: Boolean,
    default: false,
  },
  /**Отключение потери фокуса при клике по контенту */
  disableContentBlur: {
    type: Boolean,
    default: false,
  },
  /**Закрыть при выборе */
  closeBySelect: {
    type: Boolean,
    default: false,
  },
  /**Всегда показывать список */
  staticScrollHeight: { type: Boolean },
  /**Разрешено ли создание подзадачи */
  allowCreateSubtask: { type: Boolean, default: false },
});
/**События */
const emit = defineEmits<{
  /**Событие выбора задачи */
  onSelect: [taskId: number];
  /**Событие создания подзадачи */
  onCreatedSubtask: [taskId: number];
  /**Событие обновления подзадачи */
  onUpdatedSubtask: [taskId: number];
  /**Событие обновления видимости поповера */
  updateVisiblePopover: [value: boolean];
}>();

/**Ссылка на компонент мультиселекта */
const $multiselect = ref<{ close: () => void }>();
/**Ссылка на компонент шторки задач */
const $taskDrawer = ref<InstanceType<typeof TaskDrawer>>();

/**Открыть модальное окно создания подзадачи */
const openCreateSubtask = () => {
  if (props.taskId && props.allowCreateSubtask && !props.inDrawer) {
    $multiselect.value?.close();
    $taskDrawer.value?.open(
      new TaskDrawerSettingsModel({
        Mode: 'create-subtask',
        ParentTaskId: props.taskId,
      }),
    );
  }
};
/**Выбор задачи */
const onSelect = (taskId: number) => {
  emit('onSelect', taskId);
  $multiselect.value?.close();
};

/**При создании задачи */
const onCreateTask = (task: TaskModel) => {
  emit('onCreatedSubtask', task.Id);
};

/**При обновлении задачи */
const onUpdateTask = (task: TaskModel) => {
  emit('onUpdatedSubtask', task.Id);
};

/**Позиция поповера */
const popoverPosition = computed<PopoverPositionType>(() => {
  if (props.customPopoverPosition) return props.customPopoverPosition;
  if (props.isRight)
    return {
      right: '0rem',
      top: '$el:height + 0.25rem',
    };
  return {
    left: '0rem',
    top: '$el:height + 0.25rem',
  };
});
/**Путь до деталки род. задачи */
const parentTaskDetailPath = computed(() => {
  return router.resolve({
    name: 'task-detail',
    params: {
      projectId: props.editTaskInfo?.ProjectId,
      namespaceId: props.editTaskInfo?.NamespaceId,
      queueId: props.editTaskInfo?.QueueTaskId,
      taskId: props.editTaskInfo?.Id,
    },
    query: {
      isOpenCreateSubtask: 'true',
    },
  });
});

/**Опции */
const options = ref(new Array<TaskOptionModel>());
/**Отображаемы опции */
const viewOptions = computed(() =>
  options.value.filter((option) => !props.ignoreTasks.includes(option.Id)),
);
/**Загрузка */
const isLoading = ref(false);
/**Кол-во задач */
let countTasks: number | undefined;
/**Запрос */
const pageRequest = ref(
  new TasksRequestModel({
    QueueId: props.queueId,
    SearchValue: '',
    Skip: 0,
    Take: props.step,
  }),
);

/**Поиск задач */
const searchTasks = async () => {
  isLoading.value = true;
  const resp = await props.searchFunction(pageRequest.value);
  isLoading.value = false;

  if (resp.IsSuccess) {
    options.value.push(
      ...resp.Items.map(
        (task) =>
          new TaskOptionModel({
            Id: task.Id,
            Code: task.Code,
            Name: task.Name,
            StatusColor: task.Status.Color,
            StatusName: task.Status.Name,
          }),
      ),
    );
    pageRequest.value.Skip += resp.Size;
    countTasks = resp.Count;
  }
};

/**При обновлении строки поиска */
const onSearch = async () => {
  // Сброс страниц
  pageRequest.value.Skip = 0;
  isLoading.value = true;
  const resp = await props.searchFunction(pageRequest.value);
  isLoading.value = false;

  if (resp.IsSuccess) {
    options.value = resp.Items.map(
      (task) =>
        new TaskOptionModel({
          Id: task.Id,
          Code: task.Code,
          Name: task.Name,
          StatusColor: task.Status.Color,
          StatusName: task.Status.Name,
        }),
    );
    pageRequest.value.Skip += resp.Size;
    countTasks = resp.Count;
  }
};

const debounceOnSearch = debounce(onSearch, 400);

const updateSearch = (value: string) => {
  // Установка строки поиска
  pageRequest.value.SearchValue = value;

  debounceOnSearch();
};

/**
 *  Проверить проскроллен ли элемент до конца
 * @param element Элемент
 * @returns
 */
const checkScrolledToBottom = (element: HTMLElement): boolean =>
  element.scrollTop + element.clientHeight + 100 >= element.scrollHeight;

/**При скролле */
const onScoll = (event: any) => {
  const target = event.target as HTMLElement | undefined;
  if (
    !target ||
    !checkScrolledToBottom(target) ||
    isLoading.value ||
    pageRequest.value.Skip >= countTasks
  )
    return;

  searchTasks();
};

/**Сброс данных */
const reset = () => {
  pageRequest.value = new TasksRequestModel({
    SearchValue: '',
    Skip: 0,
    Take: props.step,
  });
  countTasks = undefined;
  options.value = [];
};

const onOpen = () => {
  emit('updateVisiblePopover', true);
  searchTasks();
};

const onClose = () => {
  reset();
  emit('updateVisiblePopover', false);
};
</script>

<style lang="scss" scoped>
.task-subtasks-search-dropdown {
  &__option {
    padding: 0.438rem 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    transition: 0.2s linear;
    cursor: pointer;
    &:hover {
      background: var(--neutral-translucent-5);
    }
  }
  &__option-taskid {
    display: block;
    flex-shrink: 0;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 2.125rem;
    color: var(--text-primary, #272d37);
  }
  &__option-name {
    display: block;
    font-size: 1rem;
    font-weight: 400;
    line-height: 2.125rem;
    color: var(--text-primary, #272d37);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }
  &__status {
    position: relative;
    overflow: hidden;
    flex-shrink: 0;
    padding: 0.125rem 0.5rem;
    border-radius: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    z-index: 1;
  }
  &__status-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
  }
  &__status-name {
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;
  }
  &__status-bg {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    opacity: 0.1;
    z-index: -1;
  }
}
</style>

<style lang="scss">
.task-subtasks-search-dropdown__popover {
  .tir-pm-multiselect__popover-content {
    padding-bottom: 0 !important;
  }
}
</style>
